export interface ZoneInfo {
  serviceId: string;
  serviceName: string;
  resourceId: string;
  zoneLabel: string;
  scenarioType: string;
  totalUnits: number;
  minPartySize: number;
  maxPartySize: number;
  unitCapacityMin: number;
  unitCapacityMax: number;
  pricing: { amount: number; unit: string; currency: string } | null;
}

export interface AvailabilityResult {
  hasAvailability: boolean;
  busyLevel: 'low' | 'medium' | 'high' | 'full';
  wordingKey: string;
}

import {
  API_WORKER_URL,
  venueSlugFromBrowserHost,
  withVenueHeader as ssotWithVenueHeader,
} from '@shared-endpoints';

/**
 * API base URL resolution:
 *   1. VITE_API_BASE_URL env override (dev / CI) → use it
 *   2. Production build → @shared-endpoints SSOT
 *   3. Dev (vite serve) → empty string, Vite /api proxy forwards
 *
 * Pages is static + SPA fallback, so if base is empty in prod, the SPA's
 * index.html responds to `/api/...` and JSON parse throws
 * `Unexpected token '<'`. The SSOT fallback prevents this.
 */
const rawApi =
  import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.PROD ? API_WORKER_URL : '');
const API_BASE = rawApi.replace(/\/$/, '');
const BASE = `${API_BASE}/api`;
const CREDENTIALS: RequestCredentials = 'include';

/**
 * Venue slug derivation + `x-ubc-venue-slug` header injection live in
 * @shared-endpoints so the ops-console uses the same logic (SRP+DRY).
 * Thin local wrapper passes the Vite dev fallback env var.
 */
function venueSlug(): string {
  return venueSlugFromBrowserHost({ devFallback: import.meta.env.VITE_DEV_VENUE_SLUG });
}

function defaultHeaders(extra?: HeadersInit): HeadersInit {
  const slug = venueSlug();
  const out: Record<string, string> = {};
  if (slug) out['x-ubc-venue-slug'] = slug;
  if (extra) {
    // Merge — extra wins on conflict
    const h = new Headers(extra);
    h.forEach((v, k) => {
      out[k] = v;
    });
  }
  return out;
}

// Keep ssotWithVenueHeader import referenced (for call sites that prefer
// the init-wrapper form; portal currently uses defaultHeaders pattern).
void ssotWithVenueHeader;

/**
 * Portal fetch helpers — collapse the 7-line boilerplate that was
 * repeated in every endpoint function. Error mapping is plain Error
 * (public portal doesn't have an ApiError class like ops-console
 * because guests should see simple messages, not structured codes).
 */
async function getJsonPublic<T>(path: string, label: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: CREDENTIALS,
    headers: defaultHeaders(),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `${label}: HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function postJsonPublic<T>(path: string, body: unknown, label: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: defaultHeaders({ 'content-type': 'application/json' }),
    credentials: CREDENTIALS,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `${label}: HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface PublicVenueProfile {
  slug: string;
  venueId: string;
  name: string;
  timezone: string;
  branding: {
    appTitle: string;
    tagline: string;
    tileUnitLabel: string;
    availabilityHeadline?: string;
    availabilityFootnote?: string;
    availableWording?: string;
    fullWording?: string;
    bookingTerm?: string;
    slotTerm?: string;
    paymentInstructions?: string;
    primaryColor?: string;
    logoUrl?: string;
    fontFamily?: string;
  };
  capabilities: {
    takeout: { enabled: boolean };
    publicPortal: {
      enabled: boolean;
      showTakeoutForm: boolean;
      lineId?: string;
    };
    allocation: { scenarios: readonly string[] };
  };
  zones: Array<{
    serviceId: string;
    resourceId: string;
    scenarioType: string;
    name: string;
    zoneLabel: string;
    capacity: number;
    minPartySize: number;
    maxPartySize: number;
    pricing: { amount: number; unit: string; currency: string } | null;
  }>;
}

export async function fetchPublicVenueProfile(): Promise<PublicVenueProfile> {
  return getJsonPublic('/public/venue-profile', 'venue-profile');
}

export async function fetchVenueZones(venueId: string): Promise<ZoneInfo[]> {
  const data = await getJsonPublic<{ zones: ZoneInfo[] }>(
    `/staff/venue-zones?venueId=${venueId}`,
    'venue-zones',
  );
  return data.zones;
}

export async function fetchAvailability(params: {
  venueId: string;
  serviceId: string;
  scenarioType: string;
  resourceId: string;
  partySize: number;
  startTime: string;
  endTime: string;
}): Promise<AvailabilityResult> {
  const qs = new URLSearchParams({
    venueId: params.venueId,
    serviceId: params.serviceId,
    scenarioType: params.scenarioType,
    resourceId: params.resourceId,
    partySize: String(params.partySize),
    startTime: params.startTime,
    endTime: params.endTime,
  });
  return getJsonPublic<AvailabilityResult>(`/public/availability?${qs}`, 'availability');
}

export interface TakeoutRequestInput {
  venueId: string;
  customerRef: string;
  pickupTime: string;
  itemNote: string;
}

export interface TakeoutRequestCreated {
  requestId: string;
  status: 'submitted';
}

export interface CreatePublicReservationInput {
  venueId: string;
  serviceId: string;
  scenarioType: string;
  partySize: number;
  startTime: string;
  endTime: string;
  guestContact: {
    displayName: string;
    phone?: string;
    email?: string;
    lineId?: string;
  };
  notes?: string | null;
}

export interface PublicReservationCreated {
  reservationId: string;
  guestToken: string;
  status: string;
  startTime: string;
}

export async function createPublicReservation(
  input: CreatePublicReservationInput,
): Promise<PublicReservationCreated> {
  return postJsonPublic<PublicReservationCreated>(
    '/public/reservations',
    input,
    'createPublicReservation',
  );
}

export interface PublicReservationView {
  reservationId: string;
  scenarioType: string;
  status: string;
  partySize: number;
  startTime: string;
  endTime: string;
  displayName: string;
  paymentStatus: 'unpaid' | 'partial' | 'paid' | 'refunded';
  totalAmount: number | null;
  currency: 'TWD' | null;
}

export async function fetchPublicReservation(token: string): Promise<PublicReservationView> {
  return getJsonPublic<PublicReservationView>(
    `/public/reservations/${encodeURIComponent(token)}`,
    'fetchPublicReservation',
  );
}

export async function cancelPublicReservation(
  token: string,
  reason?: string,
): Promise<{ status: string }> {
  const body: Record<string, string> = {};
  if (reason) body['reason'] = reason;
  return postJsonPublic<{ status: string }>(
    `/public/reservations/${encodeURIComponent(token)}/cancel`,
    body,
    'cancelPublicReservation',
  );
}

export async function submitTakeoutRequest(
  input: TakeoutRequestInput,
): Promise<TakeoutRequestCreated> {
  return postJsonPublic<TakeoutRequestCreated>(
    '/public/takeout-requests',
    input,
    'submitTakeoutRequest',
  );
}
